import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Search, ListTodo } from 'lucide-react';
import { api, formatDateTime, mensajeError } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import IndicadorProhibido from '../components/IndicadorProhibido';
import { IndicadorCaution } from '../components/IndicadoresSeguimiento';
import { ANCLA_SEGUIMIENTOS } from '../components/patient/SeguimientosFicha';

/**
 * «SEGUIMIENTOS» (administración, 4/11/2026)
 * ------------------------------------------
 * Menú izquierdo del CRM, para gestión (el servidor lo vuelve a comprobar:
 * GET /api/seguimientos). Dos pestañas:
 *
 *  · «En seguimiento»: los pacientes con seguimiento activo. Al lado de cada
 *    uno, la casilla «Seguimiento completado»: al marcarla pasa a la otra
 *    pestaña (POST …/completar).
 *  · «Seguimiento completado»: los ya completados.
 *
 * Las dos con buscador por paciente (nombre, apellidos, nº de historia,
 * teléfono). Pulsar en un paciente abre su ficha en el apartado
 * «Seguimientos» (`#seguimientos`). Un paciente completado vuelve a «En
 * seguimiento» cuando se le guarda una nota nueva desde la ficha.
 */
const PESTANAS = [
  { valor: 'en_seguimiento', texto: 'En seguimiento' },
  { valor: 'completado', texto: 'Seguimiento completado' },
];

export default function SeguimientosPage() {
  const [estado, setEstado] = useState('en_seguimiento');
  const [q, setQ] = useState('');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [completando, setCompletando] = useState(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      setError('');
      api.get('/api/seguimientos', { params: { estado, q: q.trim() } })
        .then((r) => { if (vivo) setDatos(r.data || { pacientes: [], cuantos: {} }); })
        .catch((e) => { if (vivo) { setDatos(null); setError(mensajeError(e, 'No se ha podido leer el listado')); } });
    }, q ? 250 : 0);
    return () => { vivo = false; clearTimeout(t); };
  }, [estado, q, recarga]);

  const completar = useCallback(async (fila) => {
    setCompletando(fila.patient_id);
    try {
      await api.post(`/api/seguimientos/pacientes/${fila.patient_id}/completar`);
      toast.success(`Seguimiento completado: ${fila.nombre}`);
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido completar el seguimiento'));
    } finally {
      setCompletando(null);
    }
  }, []);

  const cuantos = datos?.cuantos || {};
  const pacientes = datos?.pacientes || [];
  const enCompletados = estado === 'completado';

  return (
    <div>
      <PageHeader
        title="Seguimientos"
        subtitle="Pacientes con seguimiento activo y los ya completados. Pulsa en un paciente para ver sus notas."
      />

      <div className="flex flex-wrap items-center gap-2 mb-4" role="tablist" aria-label="Seguimientos">
        {PESTANAS.map((p) => {
          const activa = estado === p.valor;
          return (
            <Button
              key={p.valor}
              type="button"
              role="tab"
              aria-selected={activa ? 'true' : 'false'}
              variant={activa ? 'default' : 'outline'}
              className={activa ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
              onClick={() => setEstado(p.valor)}
              data-testid={`seguimientos-pestana-${p.valor}`}
            >
              {p.texto}{cuantos[p.valor] != null ? ` (${cuantos[p.valor]})` : ''}
            </Button>
          );
        })}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar paciente (nombre, apellidos, nº de historia, teléfono)"
          className="pl-9"
          aria-label="Buscar paciente"
          data-testid="seguimientos-buscar"
        />
      </div>

      {error ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="seguimientos-error">
          {error}. Esto no quiere decir que no haya pacientes en seguimiento.
          <Button variant="outline" size="sm" className="ml-2 h-7 text-xs" onClick={() => setRecarga((n) => n + 1)}>
            Reintentar
          </Button>
        </div>
      ) : !datos ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : !pacientes.length ? (
        <EmptyState
          icon={ListTodo}
          title={q ? 'Ningún paciente con esa búsqueda' : (enCompletados ? 'Ningún seguimiento completado' : 'Ningún paciente en seguimiento')}
          description={enCompletados ? undefined : 'Un paciente entra aquí al guardarle una nota con el botón «Seguimiento» de su ficha.'}
        />
      ) : (
        <ul className="cjo-card divide-y divide-border" data-testid="seguimientos-lista">
          {pacientes.map((f) => (
            <li key={f.patient_id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3"
              data-testid={`seguimientos-fila-${f.patient_id}`}>
              <Link
                to={`/pacientes/${f.patient_id}#${ANCLA_SEGUIMIENTOS}`}
                className="min-w-0 flex-1 hover:underline"
                data-testid={`seguimientos-paciente-${f.patient_id}`}
              >
                <span className="font-semibold text-[#13231E]">{f.nombre || '(sin nombre)'}</span>
                {f.num_historia != null && <span className="text-xs text-muted-foreground"> · Nº {f.num_historia}</span>}
                <IndicadorProhibido prohibido={f.prohibido} tamano="pequeno" interactivo={false} className="ml-1.5"
                  testId={`seguimientos-prohibido-${f.patient_id}`} />
                {f.caution && <IndicadorCaution tamano="pequeno" className="ml-1.5" testId={`seguimientos-caution-${f.patient_id}`} />}
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {enCompletados
                    ? `Completado el ${formatDateTime(f.completado_at)}${f.completado_por_nombre ? ` por ${f.completado_por_nombre}` : ''}`
                    : `Última nota: ${formatDateTime(f.ultimo_at)}`}
                  {` · ${f.episodios} nota${f.episodios === 1 ? '' : 's'}`}
                  {f.asignado_a_nombre ? ` · asignado a ${f.asignado_a_nombre}` : ' · sin asignar'}
                </span>
              </Link>
              <label className="inline-flex items-center gap-2 text-sm shrink-0 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#2E6E5E]"
                  checked={enCompletados}
                  disabled={enCompletados || completando === f.patient_id}
                  onChange={() => completar(f)}
                  data-testid={`seguimientos-completar-${f.patient_id}`}
                />
                Seguimiento completado
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
